import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert, Badge } from "react-bootstrap";
import { useApiClient } from "../../ApiService/useApiClient";
import { carePlansApi } from "../../ApiService/carePlansApi";

const safeArr = (v) => (Array.isArray(v) ? v : []);

const normalizePlan = (p) => {
  if (!p) return null;
  const id = p.id ?? p.Id;
  const title = p.title ?? p.Title ?? "";
  const status = p.status ?? p.Status ?? "";
  const effectiveFrom = p.effectiveFrom ?? p.EffectiveFrom ?? null;
  const nextReviewOn = p.nextReviewOn ?? p.NextReviewOn ?? null;
  const version = p.version ?? p.Version ?? 1;
  const summary = p.summary ?? p.Summary ?? "";
  const sectionsRaw = p.sections ?? p.Sections ?? [];
  const sections = safeArr(sectionsRaw).map((s) => ({
    type: s.type ?? s.Type ?? "",
    content: s.content ?? s.Content ?? "",
    risks: safeArr(s.risks ?? s.Risks),
  }));
  return { id, title, status, effectiveFrom, nextReviewOn, version, summary, sections };
};

const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "-");

export default function CarePlanPrintView() {
  const { planId } = useParams();
  const api = useApiClient();
  const apiWrap = useMemo(() => carePlansApi(api), [api]);

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiWrap.getById(planId);
        if (alive) setPlan(normalizePlan(res.data));
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [apiWrap, planId]);

  useEffect(() => {
    if (!loading && plan) {
      // auto open print dialog
      setTimeout(() => window.print(), 200);
    }
  }, [loading, plan]);

  if (loading) return <div className="p-4"><Spinner animation="border" /></div>;
  if (error) return <div className="p-4"><Alert variant="danger">Failed to load care plan.</Alert></div>;
  if (!plan) return <div className="p-4"><Alert variant="warning">Not found.</Alert></div>;

  return (
    <div className="careplan-print p-4">
      <style>{`
        @media print {
          body { background: #fff !important; }
          .careplan-print { padding: 0 !important; }
          .no-print { display: none !important; }
        }
        .cp-title { font-size: 22px; font-weight: 800; margin: 0; }
        .cp-meta { margin-top: 6px; color: #555; font-size: 12px; }
        .cp-section { margin-top: 18px; }
        .cp-section h3 { font-size: 14px; font-weight: 800; margin-bottom: 8px; }
        .cp-box { border: 1px solid #ddd; border-radius: 12px; padding: 12px; }
        .cp-muted { color: #555; font-size: 12px; }
        .cp-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
      `}</style>

      <div className="no-print mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>

      <div>
        <h1 className="cp-title">{plan.title}</h1>
        <div className="cp-meta">
          Status: <Badge bg="secondary">{plan.status}</Badge>{" "}
          | Version: {plan.version} | Effective: {fmt(plan.effectiveFrom)} | Next review: {fmt(plan.nextReviewOn)}
        </div>
      </div>

      <div className="cp-section">
        <h3>Summary</h3>
        <div className="cp-box">{plan.summary || <span className="cp-muted">No summary.</span>}</div>
      </div>

      {plan.sections.map((s) => (
        <div key={s.type} className="cp-section">
          <h3>{String(s.type).replace(/_/g, " ")}</h3>
          <div className="cp-box">
            {s.content ? <div dangerouslySetInnerHTML={{ __html: s.content }} /> : <span className="cp-muted">No content.</span>}
            {s.risks?.length ? (
              <div className="cp-badges">
                {s.risks.map((r) => (
                  <span key={r} className="badge bg-warning text-dark">{r}</span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
